import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  LayoutDashboard, 
  Users, 
  Settings, 
  LogOut, 
  Menu, 
  X, 
  Stethoscope, 
  ChevronRight,
  ShieldCheck,
  Zap,
  Activity,
  ChevronDown
} from "lucide-react";
import tglogo from "../../assets/logo.png";
import { fetchUserInfo } from "../../services/providerService";

const SafeAnimatePresence = AnimatePresence as any;

const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [userName, setUserName] = useState("Doctor");

  useEffect(() => {
    const getUser = async () => {
      try {
        const info = await fetchUserInfo();
        if (info?.data?.first_name) setUserName(info.data.first_name);
      } catch (e) { console.error(e); }
    };
    getUser();
  }, []);

  const menuItems = [
    { id: "teleconsult", label: "Command Center", path: "/home/teleconsult", icon: <LayoutDashboard size={22} /> },
    { id: "masters", label: "Clinical Masters", path: "/home/masters", icon: <Stethoscope size={22} /> },
    { id: "accounts", label: "My Profile", path: "/home/accounts", icon: <Users size={22} /> },
    { id: "settings", label: "System Config", path: "/home/settings", icon: <Settings size={22} /> },
  ];

  const handleLogout = () => {
    navigate("/logout");
  };

  const isActive = (path: string) => location.pathname === path;

  const sidebarVariants = {
    expanded: { width: "280px", x: 0 },
    collapsed: { width: "88px", x: 0 },
    mobileHidden: { x: "-100%" },
    mobileVisible: { x: 0 }
  };

  const SidebarContent = () => (
    <div className="flex flex-col h-full py-8 px-4 relative">
      {/* Detached Logo Section */}
      <div className={`mb-12 flex items-center ${isCollapsed ? "justify-center" : "px-4"} transition-all duration-500`}>
        <div className="relative group">
          <div className="absolute -inset-2 bg-gradient-to-r from-primary to-indigo-500 rounded-2xl blur opacity-20 group-hover:opacity-40 transition duration-500" />
          <img 
            src={tglogo} 
            alt="Logo" 
            className={`${isCollapsed ? "w-10 h-10" : "w-10 h-10"} relative z-10 object-contain transition-all duration-500`} 
          />
        </div>
        {!isCollapsed && (
          <motion.div 
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            className="ml-4"
          >
            <h2 className="text-xl font-black text-white tracking-tighter leading-none">TeleGaruda</h2>
            <p className="text-[9px] font-black text-primary uppercase tracking-[0.2em] mt-1">Provider v4.0</p>
          </motion.div>
        )}
      </div>

      {/* Navigation Links */}
      <nav className="flex-grow space-y-2">
        {menuItems.map((item) => (
          <button
            key={item.id}
            onClick={() => navigate(item.path)}
            className={`w-full group relative flex items-center gap-4 p-4 rounded-2xl transition-all duration-300 ${
              isActive(item.path) 
                ? "bg-primary text-white shadow-lg shadow-primary/25" 
                : "text-slate-400 hover:bg-white/5 hover:text-white"
            }`}
          >
            <div className={`shrink-0 transition-transform duration-300 group-hover:scale-110 ${isActive(item.path) ? "text-white" : "text-slate-500 group-hover:text-primary"}`}>
              {item.icon}
            </div>
            {!isCollapsed && (
              <motion.span 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-sm font-black tracking-tight"
              >
                {item.label}
              </motion.span>
            )}
            {isActive(item.path) && !isCollapsed && (
              <motion.div 
                layoutId="activeIndicator"
                className="absolute right-4 w-1.5 h-1.5 rounded-full bg-white shadow-[0_0_8px_white]"
              />
            )}
            
            {/* Tooltip for Collapsed State */}
            {isCollapsed && (
              <div className="absolute left-full ml-4 px-3 py-2 bg-slate-900 text-white text-[10px] font-black uppercase tracking-widest rounded-xl opacity-0 group-hover:opacity-100 pointer-events-none transition-all duration-300 translate-x-2 group-hover:translate-x-0 shadow-2xl border border-white/10 z-50">
                {item.label}
              </div>
            )}
          </button>
        ))}
      </nav>

      {/* User & Logout Section */}
      <div className="pt-8 border-t border-white/5 space-y-4">
        {!isCollapsed ? (
          <div className="bg-white/5 rounded-3xl p-4 flex items-center gap-3 border border-white/5">
             <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-indigo-600 flex items-center justify-center text-white font-black shadow-lg">
                {userName[0]}
             </div>
             <div className="overflow-hidden">
                <p className="text-xs font-black text-white truncate">Dr. {userName}</p>
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Medical Staff</p>
             </div>
          </div>
        ) : (
          <div className="flex justify-center">
             <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center text-primary font-black border border-white/5">
                {userName[0]}
             </div>
          </div>
        )}

        <button
          onClick={handleLogout}
          className={`w-full group flex items-center gap-4 p-4 rounded-2xl text-rose-400 hover:bg-rose-500/10 hover:text-rose-300 transition-all duration-300 ${isCollapsed ? "justify-center" : ""}`}
        >
          <LogOut size={22} className="shrink-0 group-hover:-translate-x-1 transition-transform" />
          {!isCollapsed && <span className="text-sm font-black tracking-tight">Sign Out</span>}
        </button>
      </div>

      {/* Collapse Toggle (Desktop) */}
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="absolute -right-4 top-1/2 -translate-y-1/2 w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center shadow-xl hover:scale-110 active:scale-95 transition-all z-50 border-4 border-slate-900 hidden lg:flex"
      >
        <ChevronRight size={16} className={`transition-transform duration-500 ${isCollapsed ? "rotate-0" : "rotate-180"}`} />
      </button>
    </div>
  );

  return (
    <>
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-slate-900 border-b border-white/5 flex items-center justify-between px-6 z-40">
        <img src={tglogo} alt="Logo" className="h-8 w-auto" />
        <button 
          onClick={() => setIsMobileOpen(!isMobileOpen)}
          className="p-2 bg-white/5 rounded-xl text-primary shadow-lg border border-white/5"
        >
          {isMobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Desktop Sidebar (Floating Glass Pillar) */}
      <motion.aside
        initial={false}
        animate={isCollapsed ? "collapsed" : "expanded"}
        variants={sidebarVariants}
        className="fixed left-6 top-6 bottom-6 bg-slate-950/90 backdrop-blur-3xl rounded-[3rem] border border-white/10 shadow-[0_32px_64px_-16px_rgba(0,0,0,0.5)] z-50 hidden lg:block overflow-visible"
      >
        <SidebarContent />
      </motion.aside>

      {/* Mobile Drawer */}
      <SafeAnimatePresence>
        {isMobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsMobileOpen(false)}
              className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm z-40 lg:hidden"
            />
            <motion.aside
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="fixed top-0 left-0 bottom-0 w-[280px] bg-slate-950 z-50 lg:hidden shadow-2xl"
            >
              <SidebarContent />
            </motion.aside>
          </>
        )}
      </SafeAnimatePresence>

      {/* Main Content Spacer for Detached Sidebar */}
      <style dangerouslySetInnerHTML={{ __html: `
        @media (min-width: 1024px) {
          main { 
            margin-left: ${isCollapsed ? "130px" : "320px"} !important; 
            transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
          }
        }
      `}} />
    </>
  );
};

export default Sidebar;
